import { useState } from "react";
import { Download, Check, RefreshCw } from "lucide-react";
import { useInstallPrompt } from "../hooks/useInstallPrompt";

// Permanent "Add to Home Screen" entry for the Settings panel. Unlike the
// banner, this never disappears — it's always here when someone's ready, and
// adapts its guidance to the device (live button on Chrome, Share-sheet steps
// on iOS, "open in your browser" inside in-app browsers).

// Settings is a canvas surface, so these follow the panel toggle. The card
// shadow drops to none on the dark canvas, where elevation is carried by
// --bg-surface sitting lighter than --bg-primary instead.
const card = { background: "var(--bg-surface)", borderRadius: 12, padding: "20px", boxShadow: "var(--shadow-soft)" };
const heading = { margin: "0 0 4px", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", textTransform: "uppercase", letterSpacing: "0.5px" };
const note = { margin: 0, fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.5 };

export function InstallCard() {
  const { deferredPrompt, installed, promptInstall, platform, recheck } = useInstallPrompt();
  const [checking, setChecking] = useState(false);
  const [rechecked, setRechecked] = useState(false);

  const handleRecheck = async () => {
    setChecking(true);
    setRechecked(false);
    await recheck();
    setChecking(false);
    setRechecked(true);
  };

  let body;
  if (installed) {
    body = (
      <>
        <div style={{ marginTop: 6, padding: "8px 12px", background: "var(--accent-quiet)", borderRadius: 8, borderLeft: "3px solid var(--accent)", display: "flex", alignItems: "center", gap: 8 }}>
          <Check size={14} color="var(--accent)" />
          <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>Brill is installed on this device</span>
        </div>
        <button onClick={handleRecheck} disabled={checking} style={{
          marginTop: 8, background: "none", border: "none", padding: "8px 6px", marginLeft: -6,
          cursor: checking ? "default" : "pointer",
          color: "var(--accent)", fontSize: 12, fontWeight: 500,
          display: "inline-flex", alignItems: "center", gap: 6, minHeight: 36,
        }}>
          <RefreshCw size={12} />
          {checking
            ? "Checking…"
            : rechecked
              ? "Still installed — re-checked just now"
              : "Deleted it? Tap to check again"}
        </button>
      </>
    );
  } else if (deferredPrompt) {
    body = (
      <button onClick={promptInstall} style={{
        display: "flex", alignItems: "center", gap: 7,
        background: "var(--btn-bg)", border: "none", borderRadius: 8,
        padding: "9px 16px", cursor: "pointer", color: "var(--btn-text)", fontSize: 13, fontWeight: 600,
      }}>
        <Download size={14} />
        Add to Home Screen
      </button>
    );
  } else if (platform === "ios") {
    body = (
      <p style={note}>
        In <strong>Safari</strong>, tap the <strong>Share</strong> icon (the square with an up-arrow), then choose <strong>Add to Home Screen</strong>.
      </p>
    );
  } else if (platform === "ios-inapp" || platform === "android-inapp") {
    body = (
      <p style={note}>
        First open Brill in your browser — tap the menu (<strong>⋯</strong>) and choose <strong>Open in browser</strong>. Then add it to your home screen from there.
      </p>
    );
  } else {
    body = (
      <p style={note}>
        In <strong>Chrome</strong> or <strong>Edge</strong>, click the install icon at the right of the address bar, or open the browser menu and choose <strong>Install Brill</strong>. On a phone, use your browser's menu and tap <strong>Add to Home screen</strong>.
      </p>
    );
  }

  return (
    <div style={card}>
      <h3 style={heading}>Install App</h3>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "var(--text-secondary)" }}>
        Add Brill to your home screen to open it like an app — full screen, one tap.
      </p>
      {body}
    </div>
  );
}
